import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";

import LendingOperationsApi from "../../../../../infrastructure/api/lendingOperations";

const OPERATIONS_SOURCE = "SolarentOperator";

const normalizePhone = (value) => {
  const digits = String(value || "").replace(/\D/g, "");
  if (digits.length === 10 && digits.startsWith("9")) return `0${digits}`;
  if (digits.startsWith("98") && digits.length === 12) return `0${digits.slice(2)}`;
  return digits;
};

/**
 * BarghPay admin OTP login modal.
 * Step 1: mobile → send OTP
 * Step 2: code → verify & persist portal session on API
 */
const BarqPortalOtpLoginModal = ({
  open,
  onClose,
  onSuccess,
  initialPhone = "",
  source = OPERATIONS_SOURCE,
}) => {
  const [step, setStep] = useState("phone"); // phone | code
  const [phone, setPhone] = useState(initialPhone || "");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setStep("phone");
    setCode("");
    setError("");
    setPhone((prev) => prev || initialPhone || "");
  }, [open, initialPhone]);

  const handleSend = async () => {
    const normalized = normalizePhone(phone);
    setError("");
    if (!/^09\d{9}$/.test(normalized)) {
      setError("شماره موبایل باید به صورت 09xxxxxxxxx باشد.");
      return;
    }

    setBusy(true);
    try {
      const res = await LendingOperationsApi.barqPortalSendOtp(source, {
        phone: normalized,
      });
      if (res?.isSuccess === false) {
        setError(res?.message || "ارسال کد تأیید ناموفق بود");
        return;
      }
      setPhone(normalized);
      setStep("code");
    } catch (e) {
      setError(e?.message || "ارسال کد تأیید ناموفق بود");
    } finally {
      setBusy(false);
    }
  };

  const handleVerify = async () => {
    const otp = String(code || "").replace(/\D/g, "");
    setError("");
    if (otp.length < 4) {
      setError("کد تأیید را وارد کنید.");
      return;
    }

    setBusy(true);
    try {
      const res = await LendingOperationsApi.barqPortalVerifyOtp(source, {
        phone,
        code: otp,
      });
      if (res?.isSuccess === false) {
        setError(res?.message || "تأیید کد ناموفق بود");
        return;
      }
      onSuccess?.(res?.data ?? res);
      onClose?.();
    } catch (e) {
      setError(e?.message || "تأیید کد ناموفق بود");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>ورود به پنل برق‌پی</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            برای خواندن وضعیت و مراحل درخواست‌های برق‌پی، با شماره موبایل مدیر وارد شوید.
            نشست پس از تأیید کد روی سرور ذخیره می‌شود.
          </Typography>

          {error ? <Alert severity="error">{error}</Alert> : null}

          {step === "phone" ? (
            <TextField
              autoFocus
              label="شماره موبایل مدیر"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="0912xxxxxxx"
              inputProps={{ inputMode: "tel", maxLength: 13 }}
              fullWidth
              disabled={busy}
            />
          ) : (
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                کد ارسال‌شده به {phone}
              </Typography>
              <TextField
                autoFocus
                label="کد تأیید"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 8))}
                inputProps={{ inputMode: "numeric", maxLength: 8 }}
                fullWidth
                disabled={busy}
                onKeyDown={(e) => e.key === "Enter" && handleVerify()}
              />
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={busy}>
          انصراف
        </Button>
        {step === "code" ? (
          <Button onClick={() => setStep("phone")} disabled={busy}>
            تغییر شماره
          </Button>
        ) : null}
        <Button
          variant="contained"
          onClick={step === "phone" ? handleSend : handleVerify}
          disabled={busy}
          startIcon={busy ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {busy
            ? "لطفاً صبر کنید..."
            : step === "phone"
            ? "ارسال کد تأیید"
            : "ورود و ذخیره نشست"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

BarqPortalOtpLoginModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  onSuccess: PropTypes.func,
  initialPhone: PropTypes.string,
  source: PropTypes.string,
};

export default BarqPortalOtpLoginModal;

export const isBarqLoginRequired = (res) => {
  const data = res?.data ?? res?.Data ?? {};
  return (
    data?.requiresLogin === true ||
    data?.RequiresLogin === true ||
    /نیاز به ورود|نشست.*منقضی|کد تأیید/i.test(String(res?.message || ""))
  );
};
